/**
 * Contenu du site (textes, images, liens).
 *
 * Le contenu vient de Strapi (`GET /api/page/home`, agrégateur), transformé vers
 * la forme de CONTENT ci-dessous. CONTENT sert de secours champ par champ :
 * CMS injoignable, média non uploadé ou champ vide → valeur locale.
 *
 * STRAPI_URL vide → contenu local uniquement, sans appel réseau.
 */

import axios from 'axios'

// Back-office Strapi Cloud. En dur : le site n'en a qu'un.
// Vider cette chaîne pour repasser entièrement sur le contenu local ci-dessous.
export const STRAPI_URL = 'https://lively-chocolate-36f7b15b80.strapiapp.com'

const PLACEHOLDER = ''

const CONTENT = {
  seo: {
    lang: 'fr',
    title: 'Templiers Valley - Un campus, des services',
    description:
      "Templiers Valley - un campus immobilier unique de 11 hectares à Sophia Antipolis : bureaux flexibles, pôle événementiel, services et qualité de vie.",
    keywords: 'Templiers Valley, campus, Sophia Antipolis, bureaux, location, coworking, événementiel, Biot',
  },

  site: {
    logo: '/assets/logo.svg',
    calendlyUrl: '#', // TODO: page de prise de rendez-vous (modifiable dans Strapi)
    calendlyLabel: 'Prenez rendez-vous',
    brochureUrl: '/assets/pdf/templiers-valley-brochure.pdf',
  },

  nav: {
    items: [
      { label: 'Le campus', anchor: '#campus' },
      { label: 'Nos services', anchor: '#services' },
      { label: 'Les accès', anchor: '#acces' },
    ],
    contactLabel: 'Nous contacter',
  },

  hero: {
    title: 'Un campus,\ndes services.',
    subtitle:
      'Souplesse, efficacité, clé en main et budget maîtrisé\npour vos bureaux, vos réunions et vos afterworks.',
    video: '', // TODO: vidéo d'introduction (en attente de livraison)
    image: '/assets/img/hero.jpg',
    ctaPrimary: { label: 'Découvrez nos bâtiments', href: '#batiments' },
    ctaSecondary: { label: 'Découvrez nos services', href: '#services' },
    marquee: ['Bureaux', 'Salles de réunion', 'Auditorium', 'Restos', 'Starbucks', 'Crèches', 'Sport', 'Event'],
  },

  campus: {
    kicker: 'Le campus',
    title: 'Des espaces sur mesure,\nultra flexibles et connectés',
    body:
      "Niché au cœur d'un **parc arboré de 11 hectares,** Templiers Valley déploie un campus immobilier unique, composé de **bâtiments indépendants, modulables** et **connectés** ainsi qu'un **pôle événementiel** inédit à Sophia Antipolis. Que vous soyez seul ou en équipe, nous saurons vous libérer des contraintes liées à vos espaces de travail grâce à des **baux de location tout compris avec engagement flexible ou des baux traditionnels.** Quelle que soit la solution retenue, nous pouvons aussi réaliser vos travaux d'aménagement, vous proposer du mobilier et des services d'entretien avec nos formules clé en main.",
    brochureLabel: 'Télécharger notre brochure',
    image: '/assets/img/campus-hall.jpg',
    advantagesTitle: 'Vos avantages clés',
    advantages: [
      { icon: '/assets/pictos/espaces-modulables.svg', label: 'Espaces\nmodulables' },
      { icon: '/assets/pictos/locaux-neufs.svg', label: 'Locaux\nneufs' },
      { icon: '/assets/pictos/location-a-la-carte.svg', label: 'Location\nà la carte' },
      { icon: '/assets/pictos/flexibilite.svg', label: 'Flexibilité\ncontractuelle' },
      { icon: '/assets/pictos/baux-evolutifs.svg', label: 'Baux\névolutifs' },
      { icon: '/assets/pictos/disponibles.svg', label: 'Disponibles\nimmédiatement' },
    ],
  },

  offers: {
    title: 'Nos offres',
    items: [
      {
        title: 'Forfaits',
        text:
          'Choisissez un **forfait clé en main avec des tarifs transparents**, ou optez pour une **gestion autonome** avec les partenaires de votre choix.',
        image: '/assets/img/offers/forfaits.jpg',
        color: 'yellow',
      },
      {
        title: 'Espaces de travail',
        text:
          'Du **bureau individuel** au **siège social clé en main**, chaque espace a été pensé pour conjuguer **confort, efficacité** et **bien-être** au quotidien.',
        image: '/assets/img/offers/espaces-de-travail.jpg',
        color: 'red',
      },
      {
        title: 'Réunions & réception',
        text:
          '**Pôle événementiel** avec salles de réunion équipées, **auditorium** de 160 places, **food court et terrasses privatisables.** Vous pourrez également profiter à chaque moment de la journée de nos services de restauration adaptés.',
        image: '/assets/img/offers/reunions-receptions.jpg',
        color: 'yellow',
      },
    ],
  },

  buildings: {
    kicker: 'Le campus',
    title: 'Nos bâtiments',
    banner: "Un véritable lieu de vie professionnelle, où l'on travaille mieux, ensemble.",
    items: [
      {
        id: 'village',
        name: 'Le Village',
        map: '/assets/maps/map-village.svg',
        tabs: [
          {
            label: 'Bâtiment',
            image: '/assets/img/buildings/village-batiment.jpg',
            caption: '17 000 m² de surface totale divisible à la location à partir de 9 m².',
          },
          {
            label: 'Bureaux',
            image: '/assets/img/buildings/village-bureaux.jpg',
            caption: 'Des bureaux lumineux et modulables.',
          },
          {
            label: 'Terrasse',
            image: '/assets/img/buildings/village-terrasse.jpg',
            caption: 'Des terrasses pensées comme de véritables espaces de respiration.',
          },
          {
            label: 'Espaces communs',
            image: '/assets/img/buildings/village-espaces-communs.jpg',
            caption: 'Accès direct aux multiples services.',
          },
        ],
      },
      {
        id: 'patio',
        name: 'Le Patio',
        map: '/assets/maps/map-patio.svg',
        tabs: [
          {
            label: 'Bâtiment',
            image: '/assets/img/buildings/patio-batiment.jpg',
            caption: '12 000 m² de surface totale divisible à la location à partir de 200 m².',
          },
          {
            label: 'Jardin',
            image: '/assets/img/buildings/patio-jardin.jpg',
            caption: 'Un jardin central arboré.',
          },
          {
            label: 'Terrasse',
            image: '/assets/img/buildings/patio-terrasse.jpg',
            caption: 'Des terrasses pensées comme de véritables espaces de respiration.',
          },
          {
            label: 'Espaces communs',
            image: '/assets/img/buildings/patio-espaces-communs.jpg',
            caption: 'Des espaces partagés qui favorisent les échanges au quotidien.',
          },
        ],
      },
      {
        id: 'passerelles',
        name: 'Les Passerelles',
        map: '/assets/maps/map-passerelles.svg',
        tabs: [
          {
            label: 'Bâtiment',
            image: '/assets/img/buildings/passerelles-batiment.jpg',
            caption: '6 000 m² de surface totale.',
          },
          {
            label: 'Terrasse',
            image: '/assets/img/buildings/passerelles-terrasse.jpg',
            caption: 'Une vue imprenable sur la Templiers Valley.',
          },
          {
            label: 'Espaces communs',
            image: '/assets/img/buildings/passerelles-espaces-communs.jpg',
            caption: 'Un accès direct aux espaces de services et de restauration.',
          },
        ],
      },
      {
        id: 'residence',
        name: 'La Résidence',
        map: '/assets/maps/map-residence.svg',
        tabs: [
          {
            label: 'Bâtiment',
            image: '/assets/img/buildings/residence-batiment.jpg',
            caption: 'Un cadre sécurisé, convivial et connecté.',
          },
          {
            label: 'Logements',
            image: '/assets/img/buildings/residence-logements.jpg',
            caption: '118 logements modernes pour étudiants et chercheurs.',
          },
        ],
      },
      {
        id: 'spot',
        name: 'Le Spot',
        map: '/assets/maps/map-spot.svg',
        tabs: [
          {
            label: '',
            image: '/assets/img/buildings/spot.jpg',
            caption: 'Notre poste de sécurité avec plus de 50 caméras.',
          },
        ],
      },
      {
        id: 'parkings',
        name: 'Parkings',
        map: '/assets/maps/map-parkings.svg',
        tabs: [
          {
            label: 'Parkings extérieurs',
            image: '/assets/img/buildings/parkings-exterieurs.jpg',
            caption: '',
          },
          {
            label: 'Parkings intérieurs',
            image: '/assets/img/buildings/parkings-interieurs.jpg',
            caption: '',
          },
        ],
        features: [
          { icon: '/assets/pictos/parkings-securises.svg', label: 'Parkings sécurisés' },
          { icon: '/assets/pictos/bornes-electriques.svg', label: 'Bornes électriques' },
          { icon: '/assets/pictos/espaces-deux-roues.svg', label: 'Espaces deux-roues' },
          { icon: '/assets/pictos/autopartage.svg', label: 'Autopartage' },
          { icon: '/assets/pictos/videosurveillance.svg', label: 'Vidéosurveillance' },
          { icon: '/assets/pictos/controle-dacces.svg', label: "Le tout sur un site clôturé avec contrôle d'accès 24H/24 7j/7" },
        ],
      },
    ],
  },

  services: {
    kicker: 'Les services',
    title: 'Lieu de travail &\nexpérience de vie',
    body:
      "Bienvenue dans un campus où tout est pensé pour simplifier la vie des entreprises et **sublimer le quotidien des collaborateurs.**\n\n**Salles de sport, restaurants/food-court, espaces bien-être, micro-crèche, coworking SPACES, laverie automatique… Ici, tout est accessible à pied. Plus besoin de courir entre deux lieux : tout est sur place, fluide, intégré, prêt à l'emploi.**\n\nUn café entre collègues, un moment de sport entre deux rendez-vous, déposer ses enfants à deux pas du bureau : ici, performance et qualité de vie vont de pair. Dans un cadre verdoyant et apaisant, **Templiers Valley réinvente l'expérience professionnelle,** en reconnectant le travail aux vrais besoins du quotidien.",
    image: '/assets/img/services/services-hero.jpg',
    categories: [
      {
        id: 'espaces',
        label: 'Espaces de travail & collaboration',
        icon: '/assets/pictos/espaces-de-travail.svg',
        items: [
          { caption: 'Bureaux individuels dès 9 m²', image: PLACEHOLDER },
          { caption: 'Un auditorium de 160 places', image: '/assets/img/services/auditorium.jpg' },
          { caption: 'Quatre salles de réunion', image: '/assets/img/services/salles-de-reunion.jpg' },
          { caption: 'Un food-court et des terrasses privatisables', image: '/assets/img/services/food-court.jpg' },
        ],
      },
      {
        id: 'food',
        label: 'Food court & convivialité',
        icon: '/assets/pictos/foodcourt.svg',
        items: [
          { caption: 'Une cafétéria', image: '/assets/img/services/cafeteria.jpg' },
          { caption: 'Un corner Starbucks', image: '/assets/img/services/corner-starbucks.jpg' },
          { caption: 'Une Pizzeria artisanale', image: '/assets/img/services/pizzeria.jpg' },
          { caption: 'Plusieurs espaces pique-nique', image: '/assets/img/services/espaces-pique-nique.jpg' },
          { caption: 'Un bar-snack', image: '/assets/img/services/bar-snack.jpg' },
          { caption: 'Une salle de repas / événementielle', image: '/assets/img/services/salle-repas.jpg' },
        ],
      },
      {
        id: 'sport',
        label: 'Bien-être & sport',
        icon: '/assets/pictos/sport.svg',
        items: [
          { caption: 'Une salle de sport avec douches et vestiaires', image: '/assets/img/services/salle-de-sport.jpg' },
          { caption: 'Un parcours sportif', image: '/assets/img/services/parcours-sportif.jpg' },
          { caption: 'Un BASIC FIT', image: PLACEHOLDER },
          { caption: 'Des espaces de relaxation', image: '/assets/img/services/espaces-relaxation.jpg' },
          { caption: 'Des terrains de pétanque et ping-pong', image: '/assets/img/services/petanque-ping-pong.jpg' },
          { caption: 'Des espaces verts aménagés', image: '/assets/img/services/espaces-verts.jpg' },
          { caption: 'Promenades dans un parc arboré', image: '/assets/img/services/promenades.jpg' },
          { caption: "Un cabinet d'ostéopathie", image: '/assets/img/services/osteopathie.jpg' },
          { caption: 'Une salle de repos', image: '/assets/img/services/salle-repos.jpg' },
          { caption: 'Une salle de danse', image: PLACEHOLDER },
        ],
      },
      {
        id: 'complementaires',
        label: 'Services complémentaires',
        icon: '/assets/pictos/services-complementaires.svg',
        items: [
          { caption: "Micro-crèche & Maison d'Assistantes Maternelles", image: '/assets/img/services/creche-mam.jpg' },
          { caption: 'Laverie automatique', image: '/assets/img/services/laverie.jpg' },
          { caption: "Station d'autopartage", image: '/assets/img/services/autopartage.jpg' },
          { caption: 'Bornes de recharge pour véhicules électriques', image: '/assets/img/services/borne-electrique.jpg' },
          { caption: 'Parkings extérieurs et espaces dédiés aux deux roues', image: PLACEHOLDER },
        ],
      },
    ],
  },

  access: {
    kicker: 'Les accès',
    title: 'Un emplacement stratégique',
    body:
      "Situé aux portes de Sophia Antipolis, entre **Nice**, **Antibes** et **Cannes**, Templiers Valley profite d'une localisation idéale, entre **mer et montagne**, dans un cadre aussi inspirant que fonctionnel.\n\nDirectement connecté aux grands axes **routiers**, **aériens** et **ferroviaires** (autoroute A8, aéroport international de Nice, gares SNCF, bus-tram sur site, pistes cyclables), le campus est très facilement accessible pour vos **équipes**, **clients**, **partenaires** et **investisseurs**.",
    map: '/assets/img/access-map.svg',
    // même illustration vectorielle en mobile (le texte passe sous la carte)
    mapMobile: '/assets/img/access-map-mobile.svg',
    // Épingles interactives de la carte des accès.
    // x / y = position en % de l'image de la carte (0 = gauche/haut, 100 = droite/bas).
    // Ces valeurs sont indépendantes de la largeur d'écran (l'image se met à l'échelle
    // proportionnellement), il suffit donc de les régler une fois ici pour le desktop.
    // Pour bouger une bulle : augmenter y = plus bas, augmenter x = plus à droite.
    // Le cercle blanc est dessiné par le composant : ces coordonnées sont celles
    // des 4 pastilles de la maquette. Pas de pastille sur Gare Biot.
    pins: [
      { id: 'aeroport', x: 56.44, y: 25.16, distance: '15 mn en voiture' },
      { id: 'gare-tgv-antibes', x: 30.89, y: 72.31, distance: '15 mn en voiture' },
      { id: 'bus-tram', x: 20.51, y: 48.61, distance: '1 mn à pieds' },
      { id: 'autoroute-a8', x: 17.37, y: 66.03, distance: '2 mn en voiture' },
    ],
  },

  contact: {
    address: '950 route des Colles, 06410 Biot/Sophia Antipolis, France',
    phone: '06 64 98 85 25',
    email: 'contact@templiers-valley.com',
    ctaLabel: 'Nous contacter',
    copyright: '2026 ©Templiers Valley',
    footerLinks: [
      { label: 'Mentions légales', href: '/mentions-legales' },
      { label: 'Politique de confidentialité', href: '/politique-de-confidentialite' },
    ],
    form: {
      title: 'Nous contacter',
      fields: {
        name: 'Nom',
        company: 'Entreprise',
        email: 'Email',
        phone: 'Téléphone',
        message: 'Message',
      },
      send: 'Envoyer',
      sending: 'Envoi…',
      success: 'Merci, votre message a bien été envoyé.',
      error: "Une erreur est survenue, merci de réessayer ou de nous écrire directement.",
    },
  },

  // Pages légales. body : une ligne par \n, une ligne commençant par « - » devient un élément de liste.
  legal: {
    kicker: 'Informations',
    title: 'Mentions légales',
    updated: 'Dernière mise à jour : 16 septembre 2026',
    intro: 'Les présentes mentions légales identifient l’éditeur du site Templiers Valley et précisent les principales conditions de consultation et d’utilisation du site.',
    backLabel: 'Retour accueil',
    sections: [
      {
        title: '1. Éditeur du site',
        body: 'Dénomination sociale : SOPHIA TEMPLIERS BUSINESS CENTRE\nForme juridique : Société à responsabilité limitée (SARL)\nCapital social : 1 euro\nSiège social : 72 rue du Faubourg Saint-Honoré, 75008 Paris, France\nÉtablissement de Biot : 950 route des Colles, 06410 Biot, France\nSIREN : 880 104 823\nRCS Paris : 880 104 823\nSIRET de l’établissement de Biot : 880 104 823 00021\nNuméro de TVA intracommunautaire : FR89880104823\nAdresse électronique : contact@templiers-valley.com\nDirecteur de la publication : François-Xavier Meaume en sa qualité de dirigeant',
      },
      {
        title: '2. Adresse du site',
        body: 'Le site est accessible à l’adresse suivante : www.templiers-valley.com',
      },
      {
        title: '3. Hébergement',
        body: 'Le site est hébergé par :\nOVH SAS\nSociété par actions simplifiée au capital de 50 000 000 euros\nSiège social : 2 rue Kellermann, 59100 Roubaix, France\nRCS Lille Métropole : 424 761 419\nTéléphone : 1007\nSite internet : www.ovhcloud.com',
      },
      {
        title: '4. Conception et réalisation',
        body: 'Conception graphique et réalisation du site : OSLO COMMUNICATION',
      },
      {
        title: '5. Propriété intellectuelle',
        body: 'Le site, sa structure générale et l’ensemble de ses contenus, notamment les textes, photographies, illustrations, vidéos, éléments graphiques, logos, marques, icônes et documents téléchargeables, sont protégés par les dispositions applicables en matière de propriété intellectuelle.\nSauf indication contraire, ces éléments appartiennent à SOPHIA TEMPLIERS BUSINESS CENTRE ou sont utilisés avec l’autorisation de leurs titulaires. Toute reproduction, représentation, adaptation, modification, diffusion ou exploitation, totale ou partielle, sans autorisation écrite préalable est interdite, sous réserve des exceptions prévues par la loi.\nLes marques et logos de tiers éventuellement présents sur le site demeurent la propriété de leurs titulaires respectifs.',
      },
      {
        title: '6. Informations publiées',
        body: 'SOPHIA TEMPLIERS BUSINESS CENTRE apporte le plus grand soin à la qualité et à l’actualisation des informations publiées. Ces informations sont fournies à titre indicatif et peuvent être modifiées à tout moment. Elles ne constituent pas une offre contractuelle, sauf mention expresse contraire.\nLes photographies, plans, perspectives, surfaces, équipements et descriptions présentés sur le site sont non contractuels, sauf engagement exprès formulé dans un document contractuel distinct.',
      },
      {
        title: '7. Disponibilité et sécurité',
        body: 'SOPHIA TEMPLIERS BUSINESS CENTRE s’efforce d’assurer l’accessibilité et le bon fonctionnement du site. L’accès peut toutefois être interrompu ou limité, notamment pour des opérations de maintenance, des mises à jour, des contraintes techniques ou des raisons de sécurité.\nL’utilisateur est responsable de son équipement, de sa connexion et des précautions nécessaires à la protection de ses systèmes et de ses données.',
      },
      {
        title: '8. Liens vers des sites tiers',
        body: 'Le site peut contenir des liens vers des services ou sites exploités par des tiers. SOPHIA TEMPLIERS BUSINESS CENTRE ne contrôle pas ces services et ne peut être tenue responsable de leur disponibilité, de leur contenu ou de leurs pratiques. L’utilisateur est invité à consulter leurs conditions d’utilisation et leurs politiques de confidentialité.',
      },
      {
        title: '9. Données personnelles et cookies',
        body: 'Les modalités de collecte et de traitement des données personnelles sont décrites dans la Politique de confidentialité, accessible depuis le pied de page du site. Les informations relatives aux cookies et autres traceurs figurent également dans cette politique et, lorsque cela est nécessaire, dans le module de gestion des préférences.',
      },
      {
        title: '10. Droit applicable',
        body: 'Le site et les présentes mentions légales sont soumis au droit français. Tout différend relatif à leur interprétation ou à leur application relève des juridictions compétentes dans les conditions prévues par la réglementation applicable.',
      },
      {
        title: '11. Contact',
        body: 'Pour toute question concernant le site, vous pouvez écrire à contact@templiers-valley.com',
      },
    ],
  },

  // Politique de confidentialité (même structure que legal).
  privacy: {
    kicker: 'Informations',
    title: 'Politique de confidentialité',
    updated: 'Dernière mise à jour : 16 septembre 2026',
    intro: 'La présente politique explique comment SOPHIA TEMPLIERS BUSINESS CENTRE collecte et utilise les données personnelles des visiteurs du site Templiers Valley, notamment lorsqu’ils adressent une demande de contact ou souhaitent organiser une visite.',
    backLabel: 'Retour accueil',
    sections: [
      {
        title: '1. Responsable du traitement',
        body: 'Le responsable des traitements décrits dans la présente politique est :\nSOPHIA TEMPLIERS BUSINESS CENTRE\nSociété à responsabilité limitée (SARL) au capital de 1 euro\nSiège social : 72 rue du Faubourg Saint-Honoré, 75008 Paris, France\nÉtablissement : 950 route des Colles, 06410 Biot, France\nSIREN : 880 104 823\nAdresse électronique : contact@templiers-valley.com\nPour toute question concernant vos données personnelles ou pour exercer vos droits, vous pouvez nous contacter à l’adresse suivante : contact@templiers-valley.com.',
      },
      {
        title: '2. Données collectées',
        body: 'Selon votre utilisation du site, les catégories de données suivantes peuvent être traitées :\n- les données d’identification et de contact, telles que le nom, l’adresse électronique et le numéro de téléphone ;\n- les données professionnelles, telles que le nom de l’entreprise ;\n- le contenu de votre demande et les informations communiquées dans votre message ;\n- les informations relatives à une demande de visite ou de rendez-vous ;\n- les données techniques nécessaires au fonctionnement et à la sécurité du site, telles que l’adresse IP, les journaux de connexion, le type de navigateur et les informations relatives au terminal ;\n- vos préférences relatives aux cookies lorsque le site utilise des traceurs soumis à votre choix.\nLes champs obligatoires sont identifiés dans le formulaire. Le nom, l’adresse électronique et le message sont nécessaires pour transmettre une demande. Le nom de l’entreprise et le numéro de téléphone sont facultatifs. En l’absence des informations obligatoires, la demande ne pourra pas être envoyée ou traitée.\nNous vous invitons à ne pas communiquer de données sensibles ni d’informations sans rapport avec votre demande.',
      },
      {
        title: '3. Utilisation et conservation des données',
        body: 'Les informations transmises au moyen du formulaire de contact sont utilisées uniquement pour :\n- répondre à votre demande ;\n- vous recontacter ;\n- organiser, le cas échéant, un rendez-vous ou une visite ;\n- assurer le suivi de nos échanges.\nLes données nécessaires au traitement de votre demande sont utilisées dans le cadre de mesures précontractuelles prises à votre initiative ou de l’intérêt légitime de SOPHIA TEMPLIERS BUSINESS CENTRE à répondre aux demandes reçues.\nLes informations relatives aux demandes de contact sont conservées pendant la durée nécessaire à leur traitement, puis pendant une durée maximale de trois ans à compter du dernier échange.\nLes données techniques collectées lors de la navigation sont utilisées pour assurer le fonctionnement et la sécurité du site. Elles sont conservées pendant une durée limitée, proportionnée à ces finalités.',
      },
      {
        title: '4. Destinataires des données',
        body: 'Peuvent accéder aux données, dans la limite de leurs attributions :\n- les personnes habilitées de SOPHIA TEMPLIERS BUSINESS CENTRE chargées de traiter les demandes ;\n- OVH SAS, en qualité d’hébergeur du site, ainsi que les prestataires assurant la maintenance, la gestion du site et sa sécurité ;\n- le prestataire de messagerie utilisé pour recevoir et traiter les demandes ;\n- le prestataire de prise de rendez-vous lorsque vous choisissez d’utiliser ce service ;\n- les autorités administratives ou judiciaires lorsqu’une obligation légale l’impose.\nLes données personnelles ne sont ni vendues ni louées à des tiers.',
      },
      {
        title: '5. Prise de rendez-vous avec un service tiers',
        body: 'Le site propose un lien vers un service externe de prise de rendez-vous : Calendly. Lorsque vous choisissez d’utiliser ce service, vous quittez le site Templiers Valley. Les données que vous communiquez sont alors également traitées selon les conditions et la politique de confidentialité du prestataire concerné.',
      },
      {
        title: '6. Transferts de données hors de l’Espace économique européen',
        body: 'Certains prestataires techniques peuvent traiter des données depuis un pays situé hors de l’Espace économique européen. Lorsque de tels transferts ont lieu, ils sont encadrés conformément à la réglementation applicable, notamment au moyen d’une décision d’adéquation de la Commission européenne ou de garanties appropriées telles que les clauses contractuelles types.',
      },
      {
        title: '7. Vos droits',
        body: 'Dans les conditions prévues par la réglementation, vous disposez des droits suivants :\n- le droit d’accéder à vos données personnelles ;\n- le droit de demander la rectification de données inexactes ou incomplètes ;\n- le droit de demander l’effacement de vos données ;\n- le droit de demander la limitation d’un traitement ;\n- le droit à la portabilité de vos données lorsque ce droit est applicable ;\n- le droit de retirer votre consentement à tout moment pour les traitements fondés sur celui-ci ;\n- le droit de vous opposer, pour des raisons tenant à votre situation particulière, à un traitement fondé sur l’intérêt légitime ;\n- le droit de vous opposer à tout moment à l’utilisation de vos données à des fins de prospection commerciale.\nVous pouvez également définir des directives relatives au sort de vos données après votre décès, dans les conditions prévues par le droit français.\nPour exercer vos droits, écrivez à contact@templiers-valley.com. Afin de protéger vos données, un justificatif d’identité pourra être demandé uniquement lorsque cela est nécessaire pour vérifier l’identité du demandeur.\nSi vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la Commission nationale de l’informatique et des libertés (CNIL), sur www.cnil.fr ou par courrier à l’adresse suivante : CNIL, 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07.',
      },
      {
        title: '8. Cookies et autres traceurs',
        body: 'Le site peut utiliser des traceurs strictement nécessaires à son fonctionnement et à sa sécurité. Ces traceurs ne nécessitent pas de consentement lorsqu’ils répondent aux conditions prévues par la réglementation.\nLorsque le site utilise des traceurs de mesure d’audience ou des services tiers soumis au consentement, ceux-ci ne sont déposés qu’après votre accord. Vous pouvez les accepter, les refuser et retirer votre consentement avec la même simplicité au moyen du module de gestion des préférences disponible sur le site.',
      },
      {
        title: '9. Sécurité',
        body: 'SOPHIA TEMPLIERS BUSINESS CENTRE met en œuvre des mesures techniques et organisationnelles appropriées afin de préserver la confidentialité, l’intégrité et la disponibilité des données personnelles et de limiter les risques d’accès non autorisé, de perte, d’altération ou de divulgation.',
      },
      {
        title: '10. Modification de la politique',
        body: 'La présente politique peut être modifiée afin de tenir compte des évolutions du site, des prestataires ou de la réglementation. La date de dernière mise à jour figure en tête du document. En cas de modification importante, une information appropriée sera publiée sur le site.',
      },
      {
        title: '11. Mention à afficher sous le formulaire',
        body: 'Les informations recueillies au moyen de ce formulaire sont traitées par SOPHIA TEMPLIERS BUSINESS CENTRE afin de répondre à votre demande et, le cas échéant, d’organiser une prise de contact ou une visite. Les champs marqués d’un astérisque sont obligatoires. Pour en savoir plus sur l’utilisation de vos données et exercer vos droits, consultez notre Politique de confidentialité.',
      },
    ],
  },
}

// ── Strapi transform ─────────────────────────────────────────────────────────
// Résout un champ média Strapi en URL absolue, sinon renvoie le fallback local.
function mediaUrl(m, fallback) {
  const url = m && (m.url || m.data?.attributes?.url)
  if (!url) return fallback ?? ''
  return /^https?:\/\//i.test(url) ? url : `${STRAPI_URL}${url}`
}
// Première valeur non vide.
function pick(v, fb) {
  return v === undefined || v === null || v === '' ? fb : v
}
// Mappe un tableau Strapi (ou retombe sur le fallback local s'il est vide).
function arr(list, fb, map) {
  return Array.isArray(list) && list.length ? list.map(map) : fb
}

function transform(page) {
  const fb = CONTENT
  const p = page || {}
  return {
    seo: {
      lang: pick(p.seo?.lang, fb.seo.lang),
      title: pick(p.seo?.title, fb.seo.title),
      description: pick(p.seo?.description, fb.seo.description),
      keywords: pick(p.seo?.keywords, fb.seo.keywords),
    },
    site: {
      logo: mediaUrl(p.site?.logo, fb.site.logo),
      calendlyUrl: pick(p.site?.calendlyUrl, fb.site.calendlyUrl),
      calendlyLabel: pick(p.site?.calendlyLabel, fb.site.calendlyLabel),
      brochureUrl: mediaUrl(p.site?.brochure, fb.site.brochureUrl),
    },
    nav: {
      items: arr(p.nav?.items, fb.nav.items, (n) => ({ label: n.label, anchor: n.anchor })),
      contactLabel: pick(p.nav?.contactLabel, fb.nav.contactLabel),
    },
    hero: {
      title: pick(p.hero?.title, fb.hero.title),
      subtitle: pick(p.hero?.subtitle, fb.hero.subtitle),
      image: mediaUrl(p.hero?.image, fb.hero.image),
      video: mediaUrl(p.hero?.video, fb.hero.video),
      ctaPrimary: {
        label: pick(p.hero?.ctaPrimary?.label, fb.hero.ctaPrimary.label),
        href: pick(p.hero?.ctaPrimary?.href, fb.hero.ctaPrimary.href),
      },
      ctaSecondary: {
        label: pick(p.hero?.ctaSecondary?.label, fb.hero.ctaSecondary.label),
        href: pick(p.hero?.ctaSecondary?.href, fb.hero.ctaSecondary.href),
      },
      marquee: pick(p.hero?.marquee, fb.hero.marquee),
    },
    campus: {
      kicker: pick(p.campus?.kicker, fb.campus.kicker),
      title: pick(p.campus?.title, fb.campus.title),
      body: pick(p.campus?.body, fb.campus.body),
      brochureLabel: pick(p.campus?.brochureLabel, fb.campus.brochureLabel),
      image: mediaUrl(p.campus?.image, fb.campus.image),
      advantagesTitle: pick(p.campus?.advantagesTitle, fb.campus.advantagesTitle),
      advantages: arr(p.campus?.advantages, fb.campus.advantages, (a, i) => ({
        icon: mediaUrl(a.icon, fb.campus.advantages[i]?.icon),
        label: a.label,
      })),
    },
    offers: {
      title: pick(p.offers?.title, fb.offers.title),
      items: arr(p.offers?.items, fb.offers.items, (o, i) => ({
        title: o.title,
        text: o.text,
        image: mediaUrl(o.image, fb.offers.items[i]?.image),
        color: o.color,
      })),
    },
    buildings: {
      kicker: pick(p.buildings?.kicker, fb.buildings.kicker),
      title: pick(p.buildings?.title, fb.buildings.title),
      banner: pick(p.buildings?.banner, fb.buildings.banner),
      items: arr(p.buildings?.items, fb.buildings.items, (b) => ({
        id: b.slug,
        name: b.name,
        map: mediaUrl(b.mapImage, ''),
        tabs: (b.tabs || []).map((t) => ({
          label: t.label || '',
          image: mediaUrl(t.image, ''),
          caption: t.caption || '',
        })),
        ...(b.features && b.features.length
          ? { features: b.features.map((f) => ({ icon: mediaUrl(f.icon, ''), label: f.label })) }
          : {}),
      })),
    },
    services: {
      kicker: pick(p.services?.kicker, fb.services.kicker),
      title: pick(p.services?.title, fb.services.title),
      body: pick(p.services?.body, fb.services.body),
      image: mediaUrl(p.services?.image, fb.services.image),
      categories: arr(p.services?.categories, fb.services.categories, (c) => ({
        id: c.slug,
        label: c.label,
        icon: mediaUrl(c.icon, ''),
        items: (c.items || []).map((it) => ({
          caption: it.caption || '',
          image: it.image ? mediaUrl(it.image, '') : '',
        })),
      })),
    },
    access: {
      kicker: pick(p.access?.kicker, fb.access.kicker),
      title: pick(p.access?.title, fb.access.title),
      body: pick(p.access?.body, fb.access.body),
      map: mediaUrl(p.access?.mapDesktop, fb.access.map),
      mapMobile: mediaUrl(p.access?.mapMobile, fb.access.mapMobile),
      pins: arr(p.access?.pins, fb.access.pins, (pin) => ({
        id: pin.pinId,
        x: Number(pin.x),
        y: Number(pin.y),
        distance: pin.distance,
      })),
    },
    contact: {
      address: pick(p.contact?.address, fb.contact.address),
      phone: pick(p.contact?.phone, fb.contact.phone),
      email: pick(p.contact?.email, fb.contact.email),
      ctaLabel: pick(p.contact?.ctaLabel, fb.contact.ctaLabel),
      copyright: pick(p.contact?.copyright, fb.contact.copyright),
      footerLinks: arr(p.contact?.footerLinks, fb.contact.footerLinks, (l) => ({
        label: l.label,
        href: l.href,
      })),
      form: {
        title: pick(p.contact?.form?.title, fb.contact.form.title),
        fields: {
          name: pick(p.contact?.form?.nameLabel, fb.contact.form.fields.name),
          company: pick(p.contact?.form?.companyLabel, fb.contact.form.fields.company),
          email: pick(p.contact?.form?.emailLabel, fb.contact.form.fields.email),
          phone: pick(p.contact?.form?.phoneLabel, fb.contact.form.fields.phone),
          message: pick(p.contact?.form?.messageLabel, fb.contact.form.fields.message),
        },
        send: pick(p.contact?.form?.sendLabel, fb.contact.form.send),
        sending: pick(p.contact?.form?.sendingLabel, fb.contact.form.sending),
        success: pick(p.contact?.form?.successMessage, fb.contact.form.success),
        error: pick(p.contact?.form?.errorMessage, fb.contact.form.error),
      },
    },
    legal: legalPage(p.legal, fb.legal),
    privacy: legalPage(p.privacy, fb.privacy),
  }
}

// Un texte légal ne se mélange pas champ par champ : la page vient entièrement
// de Strapi si elle y est complète (date + sections), sinon entièrement du local.
function legalPage(page, fb) {
  if (!page?.updated || !Array.isArray(page.sections) || !page.sections.length) return fb
  return {
    kicker: pick(page.kicker, fb.kicker),
    title: pick(page.title, fb.title),
    updated: page.updated,
    intro: page.intro || '',
    backLabel: pick(page.backLabel, fb.backLabel),
    sections: page.sections.map((s) => ({ title: s.title, body: s.body || '' })),
  }
}

let cache = null

// Récupère le contenu.
// - STRAPI_URL vide : contenu local immédiat.
// - sinon : fetch + transform, avec quelques essais (Strapi Cloud se met en
//   veille et met ~30 s à se réveiller). Si le CMS reste injoignable, on retombe
//   sur le contenu local : le site s'affiche toujours, jamais de splash infini.
const RETRY_DELAY = 1500
const REQUEST_TIMEOUT = 5000
const TOTAL_BUDGET = 12000 // au-delà, on affiche le contenu local plutôt que d'attendre

export async function fetchTemplierContent() {
  if (!STRAPI_URL) return CONTENT
  if (cache) return cache

  const deadline = Date.now() + TOTAL_BUDGET
  let attempt = 0
  while (Date.now() < deadline) {
    attempt++
    try {
      const res = await axios.get(`${STRAPI_URL}/api/page/home`, { timeout: REQUEST_TIMEOUT })
      cache = transform(res.data)
      return cache
    } catch (err) {
      console.warn(`[templiers] CMS injoignable (essai ${attempt})`, err?.message || err)
      if (Date.now() + RETRY_DELAY < deadline) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY))
      } else {
        break
      }
    }
  }

  // le contenu local est le miroir du contenu seedé : le site reste complet
  console.warn('[templiers] CMS injoignable — bascule sur le contenu local.')
  return CONTENT
}

export function getContent() {
  return CONTENT
}
